import {
  Button,
  Icons,
  Input,
  Modal,
  Table,
  Typography,
  DatePicker
} from "@pankod/refine-antd";
import { useCustom, useTranslate } from "@pankod/refine-core";
import "./tableStyle.scss";
import { IFormDatas, IFormResponse } from "interfaces";
import { ChangeEvent, useEffect, useState } from "react";
import { buttonStyle } from "styleObjects";
import * as XLSX from "xlsx";
import { defaultButtonFilled } from "components";
import moment from "moment";

const { SearchOutlined, ExportOutlined } = Icons;
type Prop = {
  record: IFormResponse;
  visible: boolean;
  callback: (e: boolean) => void;
};
type Prop2 = {
  createDate: Date;
  data: {
      [key: string]: string;
  }
};

export const TableModal = ({ record, visible, callback }: Prop) => {
  const t = useTranslate();
  const [tableData, setTableData] = useState<IFormDatas | null>();
  const [searchField, setSearchField] = useState("");
  const [dateRange, setDateRange] = useState<any>(null);
  const [filteredTableData, setFilteredTableData] = useState<Prop2[]>([]);
  const { data, isFetching } = useCustom<IFormDatas>({
    url: `forms/${record.id}/datas`,
    method: "get",
  });
  function searchFieldChange(e: ChangeEvent<HTMLInputElement>) {
    setSearchField(e.target.value);
  }

  function dateRangeChanged(value: any) {
    setDateRange(value);
  }

  function filter() {
    var filteredData = tableData?.datas
      .filter((item) => {
        if (!searchField) {
          return true;
        }
        var value = searchField.toLowerCase();

        var isFound = false;
        for (var key in item.data) {
          isFound = item.data[key] ? item.data[key].toLowerCase().includes(value) : false;
          if (isFound) return true;
        }
        return isFound;
      })
      .filter((item) => {
        if (!dateRange) {
          return true;
        }
        return moment(item.createDate).isBetween(dateRange[0].toDate(), dateRange[1].toDate());
      })
      .sort((a, b) => moment(b.createDate).valueOf() - moment(a.createDate).valueOf());

    setFilteredTableData(filteredData ?? []);
  }

  async function exportXLS() {
    if (!tableData?.datas) return;
    const dataToExport = (searchField || dateRange ? filteredTableData : tableData.datas)
      .sort((a, b) => moment(b.createDate).valueOf() - moment(a.createDate).valueOf())
      .map(item => {
        const formattedData: { [key: string]: string } = {};
        tableData.fields.forEach(field => {
          formattedData[field.label] = item.data[field.name] || '';
        });
        formattedData[t("table.createDate")] = moment(item.createDate).format("YYYY-MM-DD HH:mm");
        return formattedData;
      });
    const worksheet = XLSX.utils.json_to_sheet(dataToExport);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Sheet1");
    XLSX.writeFile(
      workbook,
      `${record.name ?? "DataExport"} ${moment().format("YYYY-MM-DD")}.xlsx`
    );
  }

  useEffect(() => {
    if (data) setTableData(data.data);
  }, [data]);

  useEffect(() => {
    filter();
  }, [searchField, dateRange]);

  return (
    <Modal
      title={
        <div
          style={{
            display: "flex",
            flexDirection: "row",
            justifyContent: "space-between",
            paddingRight: "20px",
          }}
        >
          <Typography.Text>{record.name}</Typography.Text>
          <div style={{
              display: "flex",
              gap: "14px",
            }}>
              <Input
                prefix={<SearchOutlined />}
                style={{
                  width: "inherit",
                }}
                placeholder={t("search")}
                value={searchField}
                onChange={searchFieldChange}
              />
              <DatePicker.RangePicker value={dateRange}  onChange={dateRangeChanged}  />
          </div>
        </div>
      }
      width={"80vw"}
      visible={visible}
      footer={[
        <div className="horz">
          <Button
            onClick={exportXLS}
            style={buttonStyle}
            icon={<ExportOutlined />}
          />
          <div className="fg" />
          <Button onClick={() => callback(false)} style={defaultButtonFilled}>
            {t("buttons.close")}
          </Button>
        </div>,
      ]}
      onCancel={() => callback(false)}
    >
      {tableData && (
        <Table
          loading={isFetching}
          dataSource={searchField || dateRange ? filteredTableData : tableData.datas.sort((a, b) => moment(b.createDate).valueOf() - moment(a.createDate).valueOf())}
        >
          {tableData.fields.map((item) => (
            <Table.Column
              title={item.label}
              dataIndex={"data"}
              render={(text) => text[item.name]}
              key={item.label}
              width={Math.max(11 * item.label.length, 100)}
              ellipsis={true}
            />
          ))}
          <Table.Column
              title={t("table.createDate")}
              dataIndex="createDate"
              key="createDate"
              width={Math.max(11 * t("table.createDate").length, 180)}
              render={(text) => moment(text).format("YYYY-MM-DD HH:mm")}
              ellipsis={true}
            />
        </Table>
      )}
    </Modal>
  );
};
